import { useEffect, useRef, useState } from "react";
import PageTitle from "../../components/PageTitle/PageTitle";
import './dashboard.scss';
import Select from "react-select";
import { Col, Row } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { MdAutoGraph, MdOutlinePendingActions, MdShoppingCart } from "react-icons/md";
import { BsGraphUp } from "react-icons/bs";
import { IoDocumentTextOutline } from "react-icons/io5";
import WebService from "../../utility/WebService";
import Grid, { GridColumn, GridHeader, GridRow } from "../../components/Grid/Grid";
import routePaths from "../../Navigation/routePaths";
import { useSelector } from "react-redux";
import { permissionsInterface } from "../onBoarding/onBoardingTypes";
import "react-datepicker/dist/react-datepicker.css";
import PlotDateRangePicker from "../../components/PlotDatePicker/PlotDateRangePicker";
import moment from "moment";

const headers: GridHeader[] = [
    {
        title: "Project name",
        class: "text-left",
    },
    {
        title: "Total Units",
        class: "text-left",
    },
    {
        title: "Total Booked Units",
        class: "text-left",
    },
    {
        title: "Total Available Units",
        class: "text-left",
    }
];

const agentHeaders: GridHeader[] = [
    {
        title: "Agent name",
        class: "text-left",
    },
    {
        title: "Bookings",
        class: "text-left",
    },
    {
        title: "Canceled Bookings",
        class: "text-left",
    },
    {
        title: "Unit Booked",
        class: "text-left",
    },
    {
        title: "Area Booked",
        class: "text-left",
    }
];

const Dashboard = () => {
    const navigate = useNavigate();
    const [fromDate, setFromDate] = useState(moment().startOf('day').toDate());
    const [toDate, setToDate] = useState(moment().endOf('day').toDate());
    const [dashboardData, setDashboardData] = useState<any>({});
    const [showHiddenFields, setShowHiddenFields] = useState<boolean>(false);
    const [pressedKeys, setPressedKeys] = useState(new Set());
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [projectRows, setProjectRows] = useState<GridRow[]>([]);
    const [agentRows, setAgentRows] = useState<GridRow[]>([]);
    const [projectCount, setProjectCount] = useState<number>(0);
    const [agentCount, setAgentCount] = useState<number>(0);
    const rowCompute = useRef<GridRow[]>([]);
    const nextOffset = useRef<number>(0);
    const token = useSelector((state: any) => state.OnBoardingReducer);
    const [permissions, setPermissions] = useState<permissionsInterface | null>(null);
    const [projectOptions, setProjectOptions] = useState<any[]>([]);
    const [projectId, setProjectId] = useState<any>("");

    useEffect(() => {
        getProjectOptionList();
    }, []);

    useEffect(() => {
        if (token && token.token && token.permissions && !!token.permissions.length) {
            setPermissions(token.permissions.find((item: any) => item.name === "DASHBOARD"));
        }
    }, [token]);

    useEffect(() => {
        if (fromDate && toDate && permissions && permissions.can_read) {
            getDashboardCount(fromDate, toDate, projectId);
        }
    }, [permissions, fromDate, toDate, projectId]);

    useEffect(() => {
        showHiddenFields && getDashboardCount(fromDate, toDate, projectId);
    }, [showHiddenFields]);

    useEffect(() => {
        window.addEventListener('keydown', handleKeyDown);
        window.addEventListener('keyup', handleKeyUp);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
            window.removeEventListener('keyup', handleKeyUp);
        };
    }, [pressedKeys]);

    const getDashboardCount = (start: Date, end: Date, projectId: any) => {
        setIsLoading(true);
        const startDate = start instanceof Date ? start : new Date(start);
        const endDate = end instanceof Date ? end : new Date(end);
        WebService.getAPI({ action: `get_dashboard_count?start=${startDate.toISOString()}&end=${endDate.toISOString()}&project_id=${projectId}`, is_session: showHiddenFields })
            .then((res: any) => {
                setIsLoading(false);
                setDashboardData(res);
            }).catch((error: any) => {
                setIsLoading(false);
                console.error("Error fetching dashboard data:", error);
            });
    };

    const handleKeyDown = (e: any) => {
        const key = e.key.toLowerCase();
        setPressedKeys((prev: any) => new Set([...prev, key]));
    };

    const handleKeyUp = (e: any) => {
        const key = e.key.toLowerCase();
        if (pressedKeys.has('control') && pressedKeys.has('alt') && pressedKeys.has('o') && pressedKeys.has('p')) {
            setShowHiddenFields(true);
        }
        setPressedKeys((prev: any) => { const updated = new Set(prev); updated.delete(key); return updated; });
    };

    const getProjectList = (
        offset: number,
        keyword?: string,
        startDate?: Date,
        endDate?: Date,
        order_by?: string,
        sort_by?: string
    ) => {
        nextOffset.current = offset;
        if (nextOffset.current < 0 || isLoading) {
            return;
        }
        setIsLoading(true);
        WebService.getAPI({
            action: `project-dashboard/list?limit=10&offset=${offset}&keyword=${keyword ? keyword : ""
                }&sort_by=${order_by ?? "asc"}&order_by=${sort_by ?? "created_at"}&from_date=${startDate ? startDate : ""}&to_date=${endDate ? endDate : ""
                }`,
            body: null,
            is_session: showHiddenFields
        })
            .then((res: any) => {
                setIsLoading(false);
                let rows: GridRow[] = [];
                setProjectCount(res.count);
                for (var i in res.list) {
                    let columns: GridColumn[] = [];
                    columns.push({ value: res.list[i]?.project_name ? res.list[i]?.project_name : "N/A" });
                    columns.push({ value: res.list[i]?.project_unit ? res.list[i]?.project_unit : "N/A" });
                    columns.push({ value: res.list[i].sold_unit_count ? res.list[i].sold_unit_count : "N/A" });
                    columns.push({ value: res.list[i].available_unit_count ? res.list[i].available_unit_count : "N/A" });
                    rowCompute.current.push({ data: columns });
                    rows.push({ data: columns });
                }
                setProjectRows(rows);
            })
            .catch((e) => {
                setIsLoading(false);
            });
    };

    const onSortList = (offset: number, isAscending?: boolean, key?: string) => {
        getProjectList(
            offset,
            null as any,
            null as any,
            null as any,
            isAscending ? "asc" : "desc",
            key
        );
    };

    const getAgentList = (
        offset: number,
        keyword?: string,
        startDate?: Date,
        endDate?: Date,
        order_by?: string,
        sort_by?: string
    ) => {
        nextOffset.current = offset;
        if (nextOffset.current < 0 || isLoading) {
            return;
        }
        setIsLoading(true);
        WebService.getAPI({
            action: `agent-dashboard/list?limit=10&offset=${offset}&keyword=${keyword ? keyword : ""
                }&sort_by=${order_by ?? "asc"}&order_by=${sort_by ?? "created_at"}&from_date=${startDate ? startDate : ""}&to_date=${endDate ? endDate : ""
                }`,
            body: null,
            is_session: showHiddenFields
        })
            .then((res: any) => {
                setIsLoading(false);
                let rows: GridRow[] = [];
                setAgentCount(res.count);
                for (var i in res.list) {
                    let columns: GridColumn[] = [];
                    columns.push({ value: res.list[i]?.agent_name ? `${res.list[i]?.agent_name} (${res.list[i]?.agent_phone})` : "N/A" });
                    columns.push({ value: res.list[i]?.total_bookings ? res.list[i]?.total_bookings : "N/A" });
                    columns.push({ value: res.list[i]?.cancelled_bookings ? res.list[i]?.cancelled_bookings : "N/A" });

                    columns.push({ value: res.list[i].total_unit_booked ? res.list[i].total_unit_booked : "N/A" });
                    columns.push({ value: res.list[i].total_area_booked ? res.list[i].total_area_booked : "N/A" });
                    rowCompute.current.push({ data: columns });
                    rows.push({ data: columns });
                }
                setAgentRows(rows);
            })
            .catch((e) => {
                setIsLoading(false);
            });
    };

    const onSortAgentList = (offset: number, isAscending?: boolean, key?: string) => {
        getAgentList(
            offset,
            null as any,
            fromDate,
            toDate,
            isAscending ? "asc" : "desc",
            key
        );
    };

    const getProjectOptionList = () => {
        WebService.getAPI({
            action: "get/project/list",
        }).then((res: any) => {
            var projectList = res.list.map((item: any) => {
                return { ...item, value: item.uuid, label: item.project_name };
            });
            setProjectOptions(projectList);
        });
    };


    return (
        <>
            {permissions && permissions.can_read && <div className="app-page page-dashboard">
                <div className="d-flex justify-content-between align-items-center ">
                    <PageTitle title="Dashboard" backArrow={false} active={showHiddenFields} />
                    <div className="d-flex align-items-center gap-2">
                        <div className="me-2" style={{ minWidth: '180px' }}>
                            <PlotDateRangePicker
                                start_date={fromDate}
                                end_date={toDate}
                                onDateChange={(start_date: any, end_date: any) => {
                                    setFromDate(start_date);
                                    setToDate(end_date);
                                }}
                            />
                        </div>
                        <div className="me-2" style={{ minWidth: '300px' }}>
                            <Select
                                className="basic-single w-100"
                                classNamePrefix="select"
                                isSearchable={true}
                                options={projectOptions}
                                onChange={(e: any) => {
                                    setProjectId(e ? e.value : "");
                                }}
                                value={projectOptions.find((option) => option.value == projectId) || null}
                                placeholder="Select Project"
                                isClearable
                            />
                        </div>
                    </div>
                </div>

                <Row className='g-2 mt-3'>
                    <StatCard label="Total Sale" value={dashboardData.total_sale} icon={<MdAutoGraph size={20} />} isFormate subtitle="Total sales" loading={isLoading} isClickable navigate_to={routePaths.BOOKING.path} navigate={navigate} />
                    <StatCard label="Total Expense" value={dashboardData.total_expense} icon={<MdAutoGraph size={20} />} isFormate subtitle="Total expenses" loading={isLoading} isClickable navigate_to={routePaths.EXPENSE.path} navigate={navigate} />
                    <StatCard label="Total bookings" value={dashboardData.total_bookings} icon={<MdAutoGraph size={20} />} isFormate subtitle="Total bookings" loading={isLoading} isClickable navigate_to={routePaths.BOOKING.path} navigate={navigate} />

                    <StatCard label="Total number of Booked Units" value={dashboardData.total_unit_booked} isFormate icon={<IoDocumentTextOutline size={20} />} subtitle="Total number of Booked Units" loading={isLoading} isClickable navigate_to={routePaths.BOOKED_UNITS.path} navigate={navigate} />
                    <StatCard label="Total Area Booked" value={dashboardData.total_area_booked} icon={<BsGraphUp size={20} />} subtitle="Total Area sold" loading={isLoading} isClickable navigate_to={routePaths.BOOKED_UNITS.path} navigate={navigate} />
                    <StatCard label="Total Canceled Bookings" value={dashboardData.total_canceled_bookings} icon={<MdShoppingCart size={20} />} isFormate subtitle="Total Canceled Bookings" loading={isLoading} isClickable navigate_to={`${routePaths.BOOKING.path}?status=CANCELLED`} navigate={navigate} />

                    <StatCard label="Total Registration Completed Sale" value={dashboardData.total_registration_completed_sale} icon={<MdOutlinePendingActions size={20} />} isFormate subtitle="Total Registration Completed Sale" loading={isLoading} isClickable navigate_to={routePaths.COMPLETED_REGISTRATION_UNITS.path} navigate={navigate} />
                    <StatCard label="Total Registration Pending" value={dashboardData.registration_pending_count} icon={<MdOutlinePendingActions size={20} />} isFormate subtitle="Total Registration Pending" loading={isLoading} isClickable navigate_to={routePaths.PENDING_REGISTRATION_UNITS.path} navigate={navigate} />
                    <StatCard label="Total Registration Completed" value={dashboardData.registration_completed_count} icon={<MdOutlinePendingActions size={20} />} isFormate subtitle="Total Registration Completed" loading={isLoading} isClickable navigate_to={routePaths.COMPLETED_REGISTRATION_UNITS.path} navigate={navigate} />



                    <StatCard label="Total OverDue" value={dashboardData.total_over_due} icon={<MdOutlinePendingActions size={20} />} subtitle="Total Over Due" loading={isLoading} isClickable navigate_to={`${routePaths.DUE_REPORT.path}?fromDate=${fromDate.toISOString()}&toDate=${toDate.toISOString()}`} navigate={navigate} />
                    <StatCard label="Due This Week" value={dashboardData.due_this_week} icon={<MdOutlinePendingActions size={20} />} subtitle="Total Due This Week" loading={isLoading} isClickable navigate_to={`${routePaths.DUE_REPORT.path}?fromDate=${fromDate.toISOString()}&toDate=${toDate.toISOString()}`} navigate={navigate} />
                    <StatCard label="Due This Month" value={dashboardData.due_this_month} icon={<MdOutlinePendingActions size={20} />} subtitle="Total Due This Month" loading={isLoading} isClickable navigate_to={`${routePaths.DUE_REPORT.path}?fromDate=${fromDate.toISOString()}&toDate=${toDate.toISOString()}`} navigate={navigate} />
                    
                    <StatCard label="Receipt Amount" value={dashboardData.total_reciept_amount} icon={<MdOutlinePendingActions size={20} />} subtitle="Total Receipt Amount" loading={isLoading} isClickable navigate_to={`${routePaths.RECEIPT.path}`} navigate={navigate} />
                    <Col lg={6} sm={12}>    </Col>
                    <Col lg={6} sm={12}>
                        <div className="table-card mt-3">
                            <span className="font-16 fw-bold">Project</span>
                            <Grid
                                rows={projectRows}
                                headers={headers}
                                onPageChange={getProjectList}
                                errorMessage={"No Data Found"}
                                ShowLoader={isLoading}
                                onSort={onSortList}
                                count={projectCount}
                            />
                        </div>
                    </Col>
                    <Col lg={6} sm={12}>
                        <div className="table-card mt-3">
                            <span className="font-16 fw-bold">Agents</span>
                            <Grid
                                rows={agentRows}
                                headers={agentHeaders}
                                onPageChange={getAgentList}
                                errorMessage={"No Data Found"}
                                ShowLoader={isLoading}
                                onSort={onSortAgentList}
                                count={agentCount}
                            />
                        </div>
                    </Col>
                </Row>
            </div>}
        </>
    );
};

const StatCard = ({ label, value, icon, subtitle, loading, isFormate, isClickable, navigate_to, navigate }: any) => (
    <Col lg={4} sm={12}>
        <div className={isClickable ? "bg-white rounded-2 p-3 cursor-pointer" : "bg-white rounded-2 p-3"} style={{ boxShadow: '0 0 10px rgba(0,0,0,0.1)', minHeight: '115px' }} onClick={() => isClickable && navigate(`${navigate_to}`)}>
            <>{loading ? <Skeleton count={1} height={20} /> :
                <>
                    <div className="d-flex justify-content-between align-items-start">
                        <div className="my-auto">
                            <div className="d-flex flex-column">
                                <div className="font-12 fw-bold">{label}</div>
                            </div>
                        </div>
                        <div className='ms-auto'>
                            <span className="blue-bg">{icon}</span>
                        </div>
                    </div>
                    <div className='font-bold text-black font-20'>{!isFormate ? parseFloat(value).toLocaleString(undefined, { minimumFractionDigits: 2 }) : value}</div>
                    {/* <div className='font-bold text-black font-20'>{parseFloat(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div> */}
                    {/* (exact 2 decimal show value) */}
                    {/* <div className="d-flex justify-content-between align-items-center">
                        <div className="font-12 font-regular pt-2">{subtitle}</div>
                        <div><Link to="#"><MdMoney size={20} color="#666" /></Link></div>
                    </div> */}
                </>
            }</>
        </div>
    </Col>
)

const Skeleton = ({ count, height }: { count: number; height: number }) => (
    <div className="skeleton" style={{ height, background: "linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)", backgroundSize: "200% 100%", animation: "skeleton-loading 1.5s infinite linear" }}>
        {Array.from({ length: count }).map((_, index) => (
            <div key={index} className="skeleton-line" style={{ height: "100%", width: "100%" }} />
        ))}
        <style>
            {`
                @keyframes skeleton-loading {
                    0% {
                        background-position: 200% 0;
                    }
                    100% {
                        background-position: -200% 0;
                    }
                }
            `}
        </style>
    </div>
);

export default Dashboard;
